import { EntityTypeApi } from '@portal/pkg-api/navigator'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EntityStorageType, EntityType } from '@portal/pkg-api/gen/admin'
import { AssumptionApi, EntityTypeApi as PreferencesEntityTypeApi } from '@portal/pkg-api/admin'
import { data } from '@portal/pkg-api/shared'
import { EntityTypeQueries as ClientEntityTypeQueries } from '@portal/pkg-filter-criteria'
import { ScopedEnablementQueries } from '@portal/pkg-authorization'
import { CategoryQueries } from '@admin/data/Categories'
import { AssumptionQueries } from '@admin/data/Assumptions'
import {
  AssumptionCategoryWithAssumptions,
  AssumptionEntityTypeAssocRequest,
  EntityTypeFeatureWithStorageType,
} from '@portal/pkg-api/gen/navigator'

const prefix = 'admin.entityType'

export const EntityTypeQueries = {
  entityTypes: () => ({
    queryKey: [`${prefix}.entityTypes`],
    queryFn: () => data(EntityTypeApi.getAdminEntityTypes()),
  }),
  entityCount: (entityTypeId: number) => ({
    queryKey: [`${prefix}.entityCount`, entityTypeId],
    queryFn: () => data(PreferencesEntityTypeApi.getCountOfEntitiesForEntityType(entityTypeId)),
  }),
  features: () => ({
    queryKey: [`${prefix}.features`],
    queryFn: featuresQueryFn(),
  }),
  assumptionCategoriesWithAssumptions: (entityTypeId: number) => ({
    queryKey: [`${prefix}.assumptionCategoriesWithAssumptions`, entityTypeId],
    queryFn: async () => {
      const response = data(AssumptionApi.getAssumptionCategoriesWithAssumptions(entityTypeId))
      return response ?? []
    },
  }),
  timeSeriesSubEntityTimeFrame: (entityTypeId: number) => ({
    queryKey: [`${prefix}.timeSeriesSubEntityTimeFrame`, entityTypeId],
    queryFn: async () => {
      const response = await data(EntityTypeApi.getTimeSeriesTimeFrame(entityTypeId))
      return response.length ? response : 'invalid'
    },
  }),
}

const featuresQueryFn = () => {
  return async () => {
    const response = await data(EntityTypeApi.getEntityTypeFeatures())
    const tabularFeatures = response
      .filter((f: EntityTypeFeatureWithStorageType) => f.storageType === EntityStorageType.TABULAR)
      .map(({ storageType, ...feature }) => feature)
    const eavFeatures = response
      .filter((f: EntityTypeFeatureWithStorageType) => f.storageType === EntityStorageType.EAV)
      .map(({ storageType, ...feature }) => feature)

    return { tabularFeatures, eavFeatures }
  }
}

export const queryEntityTargetCount = async (id: number) => {
  return await data(EntityTypeApi.getEntityCountByEntityType(id))
}

export const enableEntityType = async (entity: EntityType) => {
  return await data(EntityTypeApi.enableEntityType(entity.id, true, 0))
}

export const disableEntityType = async (entity: EntityType) => {
  return await data(EntityTypeApi.enableEntityType(entity.id, false, 0))
}

export const saveEntityType = async ({ entity, orgId: _orgId }: EntityTypeAndOrg) => {
  if (entity.id && entity.id !== -1) {
    return await data(EntityTypeApi.updateEntityType(entity.id, entity))
  } else {
    const newEntityType = await data(
      EntityTypeApi.createEntityType({
        ...entity,
        id: undefined as unknown as number,
      }),
    )
    return newEntityType
  }
}

export function useAdminEntityTypes() {
  return useQuery(EntityTypeQueries.entityTypes())
}

export function useReorderEntityTypes() {
  const queryClient = useQueryClient()
  return useMutation<
    { [p: string]: number },
    void,
    { [p: string]: number },
    { previousValue: EntityType[] | undefined }
  >({
    mutationFn: (orders) => data(EntityTypeApi.orderAdminEntityTypes(orders)),
    onMutate: async (newOrder) => {
      await queryClient.cancelQueries(EntityTypeQueries.entityTypes())

      const previousValue = queryClient.getQueryData<EntityType[]>(
        EntityTypeQueries.entityTypes().queryKey,
      )
      const newValue = (previousValue || []).sort((a, b) => newOrder[a.id] - newOrder[b.id])
      queryClient.setQueryData(EntityTypeQueries.entityTypes().queryKey, newValue)

      return { previousValue }
    },
    onError: (err, _variables, context) => {
      if (context) {
        queryClient.setQueryData(EntityTypeQueries.entityTypes().queryKey, context.previousValue)
      } else {
        console.error(`Error reordering entity types: ${err}`, new Error())
      }
    },
    // Typically an onSettled would exist here to refresh data from the server after optimistic update, however,
    // agGrid is not smart enough to not re-render when the rowData prop updates, but the value did not change,
    // causing a visible flash on the grid.
    // Future agGrid versions may resolve this issue, but for now trust the optimisticUpdate.onMutate code.
  })
}

export function useDisableEntityType() {
  const queryClient = useQueryClient()
  return useMutation<EntityType, unknown, EntityType>({
    mutationFn: ({ id }) => data(EntityTypeApi.enableEntityType(id, false, 0)),
    onSuccess: () => {
      void queryClient.invalidateQueries(EntityTypeQueries.entityTypes())
      void queryClient.invalidateQueries(ClientEntityTypeQueries.allEntityTypes())
    },
  })
}

export function useEnableEntityType() {
  const queryClient = useQueryClient()
  return useMutation<EntityType, unknown, EntityType>({
    mutationFn: ({ id }) => data(EntityTypeApi.enableEntityType(id, true, 0)),
    onSuccess: () => {
      void queryClient.invalidateQueries(EntityTypeQueries.entityTypes())
      void queryClient.invalidateQueries(ClientEntityTypeQueries.allEntityTypes())
    },
  })
}

export function useSaveEntityType() {
  const queryClient = useQueryClient()
  return useMutation<EntityType, unknown, EntityTypeAndOrg>({
    mutationFn: async ({ entity, orgId: _orgId }) => {
      if (entity.id && entity.id !== -1) {
        return await data(EntityTypeApi.updateEntityType(entity.id, entity))
      } else {
        return await data(
          EntityTypeApi.createEntityType({
            ...entity,
            // TODO: Fix swagger definition
            id: undefined as unknown as number,
          }),
        )
      }
    },
    onSuccess: (entityType) => {
      void queryClient.invalidateQueries(EntityTypeQueries.entityTypes())
      void queryClient.refetchQueries(ClientEntityTypeQueries.userEntityTypes())
      // A type's enablements depend on its features, and a new type needs an entry of its own.
      void queryClient.invalidateQueries(ScopedEnablementQueries.scopedEnablements())
      void queryClient.invalidateQueries(
        EntityTypeQueries.assumptionCategoriesWithAssumptions(entityType.id),
      )
    },
  })
}

export function useEntityTypeFeatures() {
  return useQuery(EntityTypeQueries.features())
}

// start fields
export function useEntityTypeAssumptionCategoryWithAssumptions(entityType: number) {
  return useQuery(EntityTypeQueries.assumptionCategoriesWithAssumptions(entityType))
}

export const querySaveEntityTypeFieldAssumptions = async (
  payload: AssumptionEntityTypeAssocRequest,
) => {
  const response = await EntityTypeApi.updateAssumptionEntityTypeAssocs(payload.entityType, payload)
  return response.data
}

export function useSaveEntityTypeFieldAssumptions() {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, AssumptionEntityTypeAssocRequest>({
    mutationFn: (payload) =>
      data(EntityTypeApi.updateAssumptionEntityTypeAssocs(payload.entityType, payload)),
    onSuccess: (data, { entityType }) => {
      void queryClient.invalidateQueries(
        EntityTypeQueries.assumptionCategoriesWithAssumptions(entityType),
      )

      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
            CategoryQueries.categoriesFilterable({ onlyFilterable: true, excludePseudo: true })
              .queryKey[0] &&
          (query.queryKey[1] as unknown as { entityTypeId: number })?.entityTypeId === entityType,
      })

      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
            AssumptionQueries.selectOptionsFilterable({
              onlyFilterable: false,
              includeNull: false,
              excludePseudo: false,
            }).queryKey[0] &&
          (query.queryKey[1] as { entityTypeId?: number } | undefined)?.entityTypeId === entityType,
      })
    },
  })
}

// end fields

// start Time Series Sub-Entity
export function useTimeSeriesSubEntityTimeFrame(entityTypeId: number) {
  return useQuery(EntityTypeQueries.timeSeriesSubEntityTimeFrame(entityTypeId))
}

// end Time Series Sub-Entity

export type EntityTypeAndOrg = {
  entity: EntityType
  orgId: number
}

export type AssumptionSelectPayload = {
  entityTypeId: number
  orgId: number
  checked: boolean
  assumptions: string[]
  newData: AssumptionCategoryWithAssumptions[]
}
